import { mapValues } from 'lodash-es'
import { filterAvailableTimes, getTimeFilterDisplayText } from '@myfunnow/web-core/time-filter'

import { useApiClient } from '@/composables/useApiClient'
import { TIME_PERIOD_I18N_MAP } from '@/constants/filters'

const TAG_NOW = 'now'
const TAG_DATE = 'date'
const TAG_TIME = 'time'
const TAG_HEAD_COUNT = 'headcount'

const filterSettingComposition = props => {
  const { $store: store, $i18n: i18n, $dayjs } = useNuxtApp()
  const filterNow = reactive({ activated: false })
  const dateMode = ref(1)
  const filterDate = reactive({ options: [] })
  const filterTime = reactive({ value: [], text: i18n.t('all_time'), activated: false })
  const filterHeadCount = reactive({ options: [] })
  const filters = reactive({ components: [] })
  const regionID = ref(props.region.id)
  const selectedCategory = reactive({})
  const categories = toRefs(props).categories
  const searchKeyWord = ref(props.searchKeyWord || '')
  const currentFilter = reactive({
    filter_regionid: regionID.value,
    category_id: selectedCategory.id || null,
    filter_keyword: searchKeyWord.value,
  })

  const timePeriodLabelMap = computed(() => mapValues(TIME_PERIOD_I18N_MAP, info => i18n.t(info.labelI18n)))

  const setSearchKeyWord = keyWord => {
    searchKeyWord.value = keyWord
    Object.assign(currentFilter, { filter_keyword: keyWord })
  }

  const fetchSearchKeywords = () => {
    const querys = { regionID: regionID.value }
    store.dispatch('search/fetchSearchKeywordsOfRegion', querys)
  }

  const fetchFilters = categoryID => {
    store
      .dispatch('filter/getFilters', { categoryID: categoryID || null, regionID: regionID.value })
      .then(res => {
        if (res.code === 0) {
          dateMode.value = res.data.day_mode
          Object.assign(filters, res.data)
          initFilterChips()
        } else {
          throw res
        }
      })
      .catch(error => {
        console.error('fetchFilters:error', error)
      })
  }

  const findFilterTag = tagName => {
    const index = filters.components.findIndex(component => {
      return component.name === tagName
    })
    let filter = index >= 0 ? filters.components[index] : {}

    if (tagName === TAG_DATE) {
      filter = index > 0 ? filters.components[index].options[2].components[0] : { options: [] }
    }

    return filter
  }

  const formatDateFilterText = starttime => {
    if (starttime) {
      return $dayjs(starttime).format('MM/DD')
    }
    return ''
  }

  const defaultFilterDate = () => {
    return {
      value: [''],
      text: '',
      keyValue: ['starttime', 'endtime'],
      options: findFilterTag(TAG_DATE).options || {},
      dayMode: dateMode.value,
      activated: false,
    }
  }

  const defaultFilterTime = () => {
    return {
      value: [],
      text: i18n.t('all_time'),
      keyValue: ['filter_times'],
      activated: false,
    }
  }

  const defaultFilterHeadCount = () => {
    return {
      value: '',
      text: '',
      keyValue: ['limit'],
      options: findFilterTag(TAG_HEAD_COUNT).options || [],
      activated: false,
    }
  }

  const deleteCurrentFilterKey = filterName => {
    switch (filterName) {
      case TAG_NOW:
        delete currentFilter.filter_time
        break
      case TAG_DATE:
        filterDate.keyValue.forEach(keyName => {
          delete currentFilter[keyName]
        })
        break
      case TAG_TIME:
        delete currentFilter.filter_times
        break
      case TAG_HEAD_COUNT:
        delete currentFilter.limit
        break
    }
  }

  const updateFilterNowValue = () => {
    const activated = !filterNow.activated
    Object.assign(filterNow, { activated })
    activated ? Object.assign(currentFilter, { filter_time: 'now' }) : deleteCurrentFilterKey(TAG_NOW)
    if (activated && filterDate.activated) {
      deleteCurrentFilterKey(TAG_DATE)
      Object.assign(filterDate, defaultFilterDate())
    }
    if (activated && filterTime.activated) {
      deleteCurrentFilterKey(TAG_TIME)
      Object.assign(filterTime, defaultFilterTime())
    }
  }

  const deactivateNowFilter = () => {
    if (filterNow.activated) {
      Object.assign(filterNow, { activated: false })
      deleteCurrentFilterKey(TAG_NOW)
    }
  }

  const updateFilterTimeValue = params => {
    const selectedTimes = params.filter_times || []
    const text = getTimeFilterDisplayText({
      selectedTimes,
      selectedDate: filterDate.value?.[0] || null,
      periodLabelMap: timePeriodLabelMap.value,
      defaultLabel: i18n.t('all_time'),
      formatExcessLabel: count => i18n.t('filter_time_display', { count }),
    })
    const activated = selectedTimes.length > 0

    Object.assign(filterTime, { value: selectedTimes, text, activated })
    if (activated) {
      deactivateNowFilter()
    }
  }

  const cleanupUnavailableFilterTimes = startDate => {
    if (filterTime.value.length === 0) {
      return
    }
    const availableTimes = filterAvailableTimes(filterTime.value, startDate)

    if (availableTimes.length === filterTime.value.length) {
      return
    }

    updateFilterTimeValue({ filter_times: availableTimes })
    if (availableTimes.length > 0) {
      Object.assign(currentFilter, { filter_times: availableTimes })
    } else {
      delete currentFilter.filter_times
    }
  }

  const updateFilterDateValue = params => {
    const text = formatDateFilterText(params.starttime)
    const startDate = params.starttime || ''
    const activated = !!startDate
    Object.assign(filterDate, { value: [startDate], text: text, activated })
    if (activated) {
      deactivateNowFilter()
    }
    if (startDate) {
      cleanupUnavailableFilterTimes(startDate)
    }
  }

  const updateFilterHeadCountValue = params => {
    const text = params.limit ? i18n.t('all_number_pax', [params.limit]) : ''
    const headCountValue = params.limit || ''
    const activated = !!headCountValue
    Object.assign(filterHeadCount, { value: headCountValue, text: text, activated })
  }

  const initFilterChips = () => {
    Object.assign(filterDate, { ...defaultFilterDate() })
    Object.assign(filterTime, { ...defaultFilterTime() })
    Object.assign(filterHeadCount, { ...defaultFilterHeadCount() })
  }

  const changedCurrentFilter = params => {
    const newParams = {}
    for (const key in params) {
      if (params[key]) {
        newParams[key] = params[key]
      } else if (currentFilter[key]) {
        delete currentFilter[key]
      }
    }
    Object.assign(currentFilter, newParams)
  }

  const updateCondition = ({ params, filterKey, activated }) => {
    changedCurrentFilter(params)
    const hasChange = Object.keys(params).length > 0
    switch (filterKey) {
      case TAG_DATE:
        if (hasChange) {
          updateFilterDateValue(params)
        }
        break
      case TAG_TIME:
        if (hasChange) {
          updateFilterTimeValue(params)
        }
        break
      case TAG_HEAD_COUNT:
        if (hasChange) {
          updateFilterHeadCountValue(params)
        }
        break
    }
  }

  const onClickCategory = ({ params }) => {
    const categoryData =
      categories.value.find(category => {
        return category.id === params.categoryID
      }) || {}
    const categoryID = categoryData.id
    if (selectedCategory.id !== categoryID) {
      initFilterChips()
      Object.assign(selectedCategory, categoryData)
      Object.assign(currentFilter, { category_id: categoryID })
      deleteCurrentFilterKey(TAG_NOW)
      deleteCurrentFilterKey(TAG_DATE)
      deleteCurrentFilterKey(TAG_HEAD_COUNT)
      deleteCurrentFilterKey(TAG_TIME)
      fetchFilters(params.categoryID)
    }
  }

  const clickNowFilter = () => {
    updateFilterNowValue()
  }

  const initSearchBar = () => {
    if (regionID.value) {
      fetchFilters()
      fetchSearchKeywords()
    }
  }

  const syncFiltersFromStore = filters => {
    if (!filters) return
    const starttime = filters.starttime || ''
    const endtime = filters.endtime || ''
    const limit = filters.limit || ''
    const filterTimes = filters.filter_times ? [].concat(filters.filter_times) : []
    const filterTime = filters.filter_time || ''

    updateCondition({
      params: { starttime, endtime },
      filterKey: 'date',
      activated: !!starttime,
    })
    updateCondition({
      params: { filter_times: filterTimes, filter_time: filterTime },
      filterKey: 'time',
      activated: filterTimes.length > 0 || filterTime === 'now',
    })
    updateCondition({
      params: { limit },
      filterKey: 'headcount',
      activated: !!limit,
    })
    Object.assign(filterNow, { activated: filterTime === 'now' })
  }

  return {
    filterNow,
    filterDate,
    filterTime,
    filterHeadCount,
    currentFilter,
    selectedCategory,
    initSearchBar,
    setSearchKeyWord,
    fetchSearchKeywords,
    fetchFilters,
    updateCondition,
    onClickCategory,
    clickNowFilter,
    syncFiltersFromStore,
  }
}

export default filterSettingComposition
